<script>
	import { createEventDispatcher, onDestroy, onMount } from "svelte";
	import { cubicOut } from "svelte/easing";
	import { fade, scale } from "svelte/transition";
	import { analytics } from "./utils/Analytics";

	export let intro = false;

	const dispatch = createEventDispatcher();

	let revealTimeout;
	
	onMount(() => {
		if (intro) {
			revealTimeout =	setTimeout(() => {
				dispatch("reveal");
			}, 5000);
		}
	});

	onDestroy(() => {
		clearTimeout(revealTimeout);
	});
</script>

<div class="about"
	on:click={() => dispatch("close")}
	transition:fade={{ duration: 500 }}>
	<div class="text">
		<h1 in:fade={{ duration: intro ? 500 : 0, delay: intro ? 500 : 0 }}>An Atlas of Emotions</h1>
		<div class="iframe" in:fade={{ duration: 500, delay: intro ? 1500 : 500 }}>
			<iframe width="560" height="315" src="https://www.youtube.com/embed/B-1xzudnueQ" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
		</div>
		<p in:fade={{ duration: intro ? 500 : 0, delay: intro ? 1800 : 0 }}>
			Over millennia, humans have depicted a vast spectrum of emotions in art that we can relate to our own experiences and which trigger feelings in us.<br /><strong>Can art help refine our understanding of complex human emotions?</strong>
		</p>
		<p in:fade={{ duration: intro ? 500 : 0, delay: intro ? 1800 : 0 }}>
			<a on:click|stopPropagation href="https://artsandculture.google.com/" target="_blank">Google Arts & Culture</a> is working with scientists from the <a on:click|stopPropagation href="https://www.berkeley.edu/" target="_blank">University of California, Berkeley</a>, to explore the emotions evoked by artworks through time, and across cultures.
		</p>
		<p in:fade={{ duration: intro ? 500 : 0, delay: intro ? 1800 : 0 }}>
			We asked <strong>1,300 people</strong> to describe how <strong>1,500 paintings</strong> make them feel by choosing from different words. The results revealed <strong>25</strong> different emotions that people linked to the artworks they saw. We plotted these feelings on an interactive map, grouping artworks that triggered specific emotions.
		</p>
		<p in:fade={{ duration: intro ? 500 : 0, delay: intro ? 1800 : 0 }}>
			This atlas of emotions will help scientists at the University of California, Berkeley, to advance the scientific study of human emotion.
		</p>
		<p in:fade={{ duration: intro ? 500 : 0, delay: intro ? 1800 : 0 }}>
			Explore a complex palette of human emotion, <strong>how do the colors, brushstrokes, shapes, and subjects	make you feel?</strong>
		</p>
		{#if !intro}
			<a in:fade={{ duration: intro ? 500 : 0, delay: intro ? 1800 : 0 }} on:click|stopPropagation class="partner" href="https://www.berkeley.edu/" target="_blank">
				<img src="./assets/images/berkeley.svg" alt="Berkeley" />
			</a>
		{/if}
	</div>
	<footer in:fade={{ duration: intro ? 500 : 0, delay: intro ? 2100 : 0 }}>
		{#if intro}
			<div class="button" data-label="Explore" on:click={() => dispatch("start")}></div>
		{:else}
			<div class="button"
				data-label="Share"
				on:click|stopPropagation={() => {
					labshare.openDialog({
						title: "Art Emotions Map",
						url: "https://experiments.withgoogle.com/art-emotions-map",
						text: "Explore this atlas of emotions and see how art makes you feel. A project by @googlearts and University of California, Berkeley.",
					});
					analytics.openSharePanel();
				}}></div>
			<a class="button"
				href="https://policies.google.com/privacy"
				target="_blank"
				data-label="Privacy &amp; terms"
				on:click|stopPropagation></a>
		{/if}
	</footer>
</div>

<style>
	.about {
		display: flex;
		align-items: center;
		flex-direction: column;
		justify-content: center;
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: 10;
		/* background-color: rgba(255, 255, 255, 0.95); */
	}

	.about::before {
		content: "";
		display: block;
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		z-index: -1;
		background: radial-gradient(circle,
			hsl(0, 0%, 100%) 50%,
			hsla(0, 0%, 100%, 0.997) 54.05%,
			hsla(0, 0%, 100%, 0.99) 57.75%,
			hsla(0, 0%, 100%, 0.979) 61.25%,
			hsla(0, 0%, 100%, 0.965) 64.5%,
			hsla(0, 0%, 100%, 0.948) 67.65%,
			hsla(0, 0%, 100%, 0.93) 70.6%,
			hsla(0, 0%, 100%, 0.91) 73.55%,
			hsla(0, 0%, 100%, 0.89) 76.45%,
			hsla(0, 0%, 100%, 0.87) 79.4%,
			hsla(0, 0%, 100%, 0.852) 82.35%,
			hsla(0, 0%, 100%, 0.835) 85.5%,
			hsla(0, 0%, 100%, 0.821) 88.75%,
			hsla(0, 0%, 100%, 0.81) 92.25%,
			hsla(0, 0%, 100%, 0.803) 95.95%,
			hsla(0, 0%, 100%, 0.8) 100%
		);
		opacity: 0.95;
	}

	.text {
		width: 100%;
		max-height: 100%;
		padding: 0.75rem;
		overflow: auto;
		-webkit-mask-image: linear-gradient(to top, rgba(0,0,0,0), rgba(0,0,0,1) 2rem);
		mask-image: linear-gradient(to top, rgba(0,0,0,0), rgba(0,0,0,1) 2rem);
	}

	.text>* {
		max-width: 600px;
		margin: 0 auto;
		padding: 0.75rem;
	}

	h1 {
		line-height: 2;
		text-align: center;
	}

	h1, strong {
		font-weight: 500;
	}

	a {
		color: black;
	}

	.iframe {
		width: 100%;
		position: relative;
	}

	.iframe::before {
		content: "";
		display: block;
		padding-top: 56.25%;
	}

	iframe {
		display: block;
		width: calc(100% - 1.5rem);
		height: calc(100% - 1.5rem);
		position: absolute;
		top: 0.75rem;
		right: 0.75rem;
		bottom: 0.75rem;
		left: 0.75rem;
	}

	.partner {
		display: block;
		width: 10em;
		height: 6em;
	}

	.partner>img {
		display: block;
		width: 100%;
	}

	footer {
		margin: 1rem 0 2rem;
		text-align: center;
	}

	.button {
		display: inline-block;
    height: 2.5em;
    margin: 0 0.5rem;
    padding: 0 1.5em;
		position: relative;
    background: linear-gradient(35deg, #68caf4 0%, #c5f5ff 100%);
    border-radius: 2.5em;
    line-height: 2.5em;
    text-align: center;
		text-decoration: none;
		cursor: pointer;
	}

	.button::before {
		content: attr(data-label);
		position: relative;
		z-index: 1;
		font-weight: 500;
	}

	.button::after {
		content: "";
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
    border-radius: 2.5em;
		background: linear-gradient(-35deg, #fbab7e 0%, #f7ce68 100%);
		/* mix-blend-mode: lighten; */
		opacity: 0;
		transition: opacity 0.15s ease-in-out;
	}

	.button:hover::after {
		opacity: 1;
	}

	@media (max-width: 800px) {
		h1 {
			font-size: 1.5em;
			line-height: 1.5;
		}

		footer {
			margin-bottom: 1rem;
		}
	}

	@media (max-width: 480px) {
		.text {
			padding: 0.5rem;
		}

		.text>* {
			padding: 0.5rem;
		}

		iframe {
			width: calc(100% - 1rem);
			height: calc(100% - 1rem);
			top: 0.5rem;
			right: 0.5rem;
			bottom: 0.5rem;
			left: 0.5rem;
		}
	}
</style>